import { CheckIcon } from "@chakra-ui/icons";
import { ButtonProps } from "@chakra-ui/react";
import { MdOutlineEdit } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";

import { ButtonWrapper, CancelIcon, PreviewIcon, RanaButton } from "@nens/ranalib";

interface EditCancelSubmitButtonsProps {
  editButtonProps?: ButtonProps;
  submitButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  isEditing: boolean;
  isMutating: boolean;
}

interface PreviewingButtonProps extends EditCancelSubmitButtonsProps {
  isPreviewing?: boolean;
  previewButtonProps?: ButtonProps;
}

const DEFAULT_BUTTON_PROPS: ButtonProps = {
  color: "brand.analytic",
  variant: "ghost",
  bg: "transparent",
  h: "button",
};

const getButtonProps = (props: ButtonProps | undefined) =>
  props?.variant ? undefined : DEFAULT_BUTTON_PROPS;

export function EditCancelSubmitButtons(
  props: EditCancelSubmitButtonsProps | PreviewingButtonProps
) {
  const {
    isEditing,
    editButtonProps = {},
    submitButtonProps = {},
    cancelButtonProps = {},
    isMutating,
  } = props;

  const hasPreviewButton = "isPreviewing" in props;
  const isPreviewing = hasPreviewButton ? props.isPreviewing : undefined;
  const previewButtonProps = "previewButtonProps" in props ? props.previewButtonProps : undefined;

  const intl = useIntl();

  return (
    <ButtonWrapper>
      {/* CANCEL */}
      {isEditing && (
        <RanaButton
          isLoading={isMutating}
          {...getButtonProps(cancelButtonProps)}
          leftIcon={<CancelIcon />}
          title={intl.formatMessage({
            id: "button.cancel_edit",
            defaultMessage: "Cancel editing",
          })}
          {...cancelButtonProps}
        >
          {cancelButtonProps.children ?? (
            <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
          )}
        </RanaButton>
      )}

      {/* PREVIEW */}
      {isEditing && hasPreviewButton && !isPreviewing && previewButtonProps && (
        <RanaButton
          isLoading={isMutating}
          {...getButtonProps(previewButtonProps)}
          leftIcon={<PreviewIcon />}
          title={intl.formatMessage({
            id: "button.preview_edit",
            defaultMessage: "Preview edits",
          })}
          {...previewButtonProps}
        >
          {previewButtonProps.children ?? (
            <FormattedMessage id="button.preview" defaultMessage="Preview" />
          )}
        </RanaButton>
      )}

      {/* START EDIT */}
      {(!isEditing || isPreviewing) && (
        <RanaButton
          isLoading={isMutating}
          {...getButtonProps(editButtonProps)}
          leftIcon={<MdOutlineEdit />}
          title={intl.formatMessage({
            id: "file.start_edit",
            defaultMessage: "Start editing",
          })}
          {...editButtonProps}
        >
          {editButtonProps.children ?? <FormattedMessage id="button.edit" defaultMessage="Edit" />}
        </RanaButton>
      )}

      {/* SAVE */}
      {isEditing && (
        <RanaButton
          isLoading={isMutating}
          {...getButtonProps(submitButtonProps)}
          leftIcon={<CheckIcon />}
          title={intl.formatMessage({
            id: "button.save_edit",
            defaultMessage: "Save edits",
          })}
          {...submitButtonProps}
        >
          {submitButtonProps.children ?? (
            <FormattedMessage id="button.save" defaultMessage="Save" />
          )}
        </RanaButton>
      )}
    </ButtonWrapper>
  );
}
